import { KnownProjectIds, RemoteConfigProperties } from "./types";
export const PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS = new Set([KnownProjectIds.MAD_MASTER]);
/**
 * Base set of URLS used throughout of the UI. These are based on project ids.
 * @constructor {googleEndpointProjectId} - Project ID to build URL with.
 */
export default class URLS {
    constructor(sdk, config) {
        this.sdk = sdk;
        const { googleEndpointProjectId } = config;
        this.baseCIUrl = config.baseCIUrl;
        this.googleEndpointProjectSuffix = googleEndpointProjectId.replace("mad-", "");
        this.googleEndpointProjectId = googleEndpointProjectId;
        this.googleEndpointProjectIdWithFallback = googleEndpointProjectId;
        // TODO: Remove once 'talon' has been fixed
        if (this.googleEndpointProjectSuffix === "talon") {
            this.googleEndpointProjectSuffix = "qa";
            this.googleEndpointProjectIdWithFallback = KnownProjectIds.MAD_QA;
        }
    }
    /**
     * URL for base API (NNY)
     */
    get baseAPIUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/campaign-management/v1`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? "https://api.madhive.com/campaign-management/v1"
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/campaign-management/v1`;
    }
    /**
     * URL for Planner Service
     * We've decoupled planner from hermes to it's own service
     */
    get basePlannerServiceUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/planning/v1`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/planning/v1`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/planning/v1`;
    }
    /**
     * URL for Hermes (to be deprecated for v2)
     */
    get baseHermesUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/reporting/v1`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/reporting/v1`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/reporting/v1`;
    }
    /**
     * URL for Hermes static information.
     */
    get baseHermesUrlStaticAssets() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/static/v1`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/static/v1`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/static/v1`;
    }
    /**
     * URL for Hermes static information, v2. For temporary use; will eventually phase out phase 1 so this becomes baseHermesUrlStaticAssets
     */
    get baseHermesUrlStaticAssetsTempV2() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/static/v2`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/static/v2`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/static/v2`;
    }
    /**
     * URL for Reporting
     */
    get madhiveReportingBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/reporting/v2`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? "https://api.madhive.com/reporting/v2"
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/reporting/v2`;
    }
    /**
     * URL for Creatives in MadHive Encoder
     */
    get madhiveEncoderBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/creatives/v1`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? "https://api.madhive.com/creatives/v1"
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/creatives/v1`;
    }
    /**
     * URL for PII
     */
    get madhivePIIBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/pii-ingest`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? "https://api.madhive.com/pii-ingest"
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/pii-ingest`;
    }
    /**
     * URL for the creative asset folder
     */
    get creativeAssetFolderBaseUrl() {
        return PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
            ? "https://z.mdhv.io"
            : `https://storage.googleapis.com/${this.googleEndpointProjectId}-creative-assets`;
    }
    /**
     * URL for adbook campaigns
     */
    get adbookCampaignsBaseUrl() {
        return PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
            ? `https://api.madhive.com/adbook`
            : // Staging Edge url for adbook (doesn't exist on hack/qa)
                `https://api-${this.googleEndpointProjectSuffix}.madhive.com/adbook`;
    }
    /**
     * URL for inscape
     */
    get inscapeBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/inscape/v1/status`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/inscape/v1/status`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/inscape/v1/status`;
    }
    /**
     * URL for identity.
     *
     * Distinct from burnsBaseUrl on purpose: paths under /burns/ that identity has
     * taken over are rollout-gated at edge and fall back to burns on an identity
     * 5xx, so a request whose semantics only identity implements must address
     * identity directly rather than ride the gate.
     */
    get identityBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/identity/v1`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/identity/v1`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/identity/v1`;
    }
    get burnsBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/burns`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/burns`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/burns`;
    }
    /**
     * URL for looker-gateway's reporting API. Edge routes `/looker/v1/query/`
     * straight through without stripping a prefix, so the version segment belongs
     * here rather than in each endpoint path.
     */
    get lookerGatewayBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/looker/v1`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/looker/v1`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/looker/v1`;
    }
    /**
     * URL for the Maverick Seller Service.
     *
     * Version prefix (e.g. `/v1`) stays in each endpoint path, not here — that
     * mirrors the mad-types-generated fetcher urls (`/v1/proposal`, ...) and
     * keeps a single base URL per service regardless of API version.
     */
    get sellerBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/seller`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/seller`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/seller`;
    }
    /**
     * URL for the Creative Service.
     *
     * Creative serves its routes under `/api/v1/creative` natively, so that
     * prefix stays in the endpoint paths rather than here — edge strips only the
     * `/creative` segment before proxying.
     */
    get creativeBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/creative`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/creative`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/creative`;
    }
    get insightSlidesBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/insight-slides`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? "https://api.madhive.com/insight-slides"
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/insight-slides`;
    }
    get wideorbitErrorsBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/wideorbit/v1/error/report`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/wideorbit/v1/error/report`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/wideorbit/v1/error/report`;
    }
    themeVarsUrl(orgName) {
        const apiDomain = this.baseCIUrl || PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
            ? "https://api.madhive.com"
            : `https://api-${this.googleEndpointProjectSuffix}.madhive.com`;
        return `${apiDomain}/burns/styles/${orgName}.css`;
    }
    get advancedQueryToolBaseUrl() {
        return this.baseCIUrl
            ? `${this.baseCIUrl}/advancedquerytool`
            : PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId)
                ? `https://api.madhive.com/advancedquerytool`
                : `https://api-${this.googleEndpointProjectSuffix}.madhive.com/advancedquerytool`;
    }
    /** The Madhive looker host, minus any connection protocol. */
    get lookerHost() {
        switch (this.googleEndpointProjectId) {
            case KnownProjectIds.MAD_HACK:
                return "madhiveexternaldev.cloud.looker.com";
            case KnownProjectIds.MADHIVE_TESTING:
                return "madhiveexternaldev.cloud.looker.com";
            case KnownProjectIds.MAD_QA:
                return "madhiveexternalstage.cloud.looker.com";
            case KnownProjectIds.MAD_STAGING:
                return "madhiveexternalstage.cloud.looker.com";
            default:
                return `madhiveexternal.cloud.looker.com`;
        }
    }
    /** A full, base URL to the Madhive looker instance. */
    get lookerBaseUrl() {
        return `https://${this.lookerHost}`;
    }
    /** The url for campaign/lineitem pacing summary data. */
    get pacingUrl() {
        const pacingEndpoint = this.sdk.featureFlags.isFlagEnabled(RemoteConfigProperties.PACE_TO_BUDGET_REPORTING)
            ? "campaign_impression_summary_p2b_v2"
            : "campaign_impression_summary_v2";
        return `${this.sdk.urls.baseHermesUrl}/${pacingEndpoint}`;
    }
    /**
     * Legacy Base URL for AI Agents.
     */
    get baseAiAgentsUrlLegacy() {
        const isProductionProject = PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId);
        const host = isProductionProject
            ? "api2.madhive.com"
            : `api2-${this.googleEndpointProjectSuffix}.madhive.com`;
        return `https://${host}/agents`;
    }
    /**
     * Base URL for AI Agents (ADK / Vertex Agent Engine) endpoints.
     */
    get baseAiAgentsUrl() {
        const isProductionProject = PRODUCTION_GOOGLE_ENDPOINT_PROJECT_IDS.has(this.googleEndpointProjectId);
        const host = isProductionProject
            ? "agent.madhive.com"
            : `agent.${this.googleEndpointProjectSuffix}.madhive.com`;
        return `https://${host}/v1/agent`;
    }
}
//# sourceMappingURL=urls.js.map